import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import {
    RICH_BLOCK_STATUSES,
    IRichBlockTable,
    IRichBlockTableColumn,
    IRichBlockTableRow,
    RichBlockCellValue,
    RichBlockStatus,
} from '@pure1/data';
import { ToastService } from '@pure/hive';

import { renderRichBlockText } from '../../../model/rich-block-text';
import { downloadTableBlockCsv } from './table-block-csv';
import { FormatSizePipe } from '../../../../ui/pipes/format-size.pipe';
import { RichBlockStatusComponent } from '../rich-block-status/rich-block-status.component';

/** A `datetime` carrying no time of day, which is formatted as a date and not as midnight. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Right-aligned kinds: a column of figures is read by comparing digits in the same place. */
const NUMERIC_KINDS: readonly string[] = ['number', 'percent', 'bytes'];

/**
 * A small result set as a table (R2.2).
 *
 * The server caps this at ten rows and may send `total_rows` to say how many there really are, so
 * the block is a sample with a stated size rather than a paginated grid. It sorts its own rows
 * (FR-027), which reorders the sample and nothing else; there is deliberately no filtering and no
 * page control, because neither can mean anything over ten rows the server already chose.
 *
 * Severity arrives on two independent axes (FR-045): `_emphasis` marks a whole row, and
 * `_cell_emphasis` marks named cells within it. They are additive, so a critical row may carry a
 * warning cell and both must show.
 */
@Component({
    selector: 'copilot-rich-block-table',
    templateUrl: './table-block.component.html',
    styleUrl: './table-block.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    imports: [RichBlockStatusComponent],
})
export class TableBlockComponent {
    private readonly toast = inject(ToastService);

    /** The column being sorted on, and which way. `null` means declared order. */
    private readonly sort = signal<{ key: string; descending: boolean } | null>(null);
    // Constructed, not injected: neither pipe is declared in the template, so putting them in
    // `providers` would add DI wiring for two stateless formatters. `FormatSizePipe` does the same.
    private readonly decimal = new DecimalPipe('en-US');
    private readonly size = new FormatSizePipe();

    readonly block = input.required<IRichBlockTable>();

    /**
     * The true size of the result set. `total_rows` is optional and the contract says it is
     * `>= rows.length`; a server that contradicts that would otherwise render "showing 10 of 3",
     * so the larger of the two wins and the footer stays truthful either way.
     */
    readonly totalRows = computed(() => {
        const rows = this.block().rows.length;
        return Math.max(this.block().total_rows ?? rows, rows);
    });

    /** Whether the table is a sample, which is the only case that earns a footer. */
    readonly isTruncated = computed(() => this.totalRows() > this.block().rows.length);

    /**
     * The rows as displayed: declared order until the reader sorts (FR-027).
     *
     * Sorting is over the rows the block carries and nothing else -- the block is a sample, so
     * this reorders ten rows, it does not rank the result set. No request, no chat turn.
     */
    readonly displayRows = computed<IRichBlockTableRow[]>(() => {
        const sort = this.sort();
        const rows = this.block().rows;
        if (!sort) {
            return rows;
        }
        const column = this.block().columns.find(c => c.key === sort.key);
        if (!column) {
            return rows;
        }
        // A copy: `rows` belongs to the segment the message holds, and sorting in place would
        // change what copy and export read back.
        return [...rows].sort((a, b) => this.compare(a, b, column, sort.descending));
    });

    sortedBy(column: IRichBlockTableColumn): 'ascending' | 'descending' | 'none' {
        const sort = this.sort();
        if (sort?.key !== column.key) {
            return 'none';
        }
        return sort.descending ? 'descending' : 'ascending';
    }

    /** Click once to sort ascending, again for descending, a third time to restore declared order. */
    toggleSort(column: IRichBlockTableColumn): void {
        const sort = this.sort();
        if (sort?.key !== column.key) {
            this.sort.set({ key: column.key, descending: false });
        } else if (!sort.descending) {
            this.sort.set({ key: column.key, descending: true });
        } else {
            this.sort.set(null);
        }
    }

    /** FR-047: the block's own copy, yielding the same markdown the answer-level copy uses. */
    copy(): void {
        navigator.clipboard
            .writeText(renderRichBlockText(this.block()))
            .then(() => this.toast.addToast({ message: 'Table copied', severity: 'default' }))
            .catch((error: unknown) => {
                this.toast.addToast({ message: 'Failed to copy table', severity: 'major' });
                console.error('Failed to copy table to clipboard', error);
            });
    }

    /**
     * FR-027: the rows the block carries, as a CSV file.
     *
     * Beside Copy rather than instead of it: copy puts markdown on the clipboard for a message or
     * a ticket, export produces a file a spreadsheet can open. The cells are the ones on screen --
     * `displayValue` is passed through, so a byte count exports as it reads.
     */
    downloadCsv(): void {
        try {
            downloadTableBlockCsv(this.block(), (row, column) => this.displayValue(row, column));
        } catch (error: unknown) {
            // A failed export is silent otherwise: the click produces no file and no message, and
            // the reader is left wondering whether they missed the download.
            this.toast.addToast({ message: 'Failed to export table', severity: 'major' });
            console.error('Failed to export table as CSV', error);
        }
    }

    isNumeric(column: IRichBlockTableColumn): boolean {
        return NUMERIC_KINDS.includes(column.kind);
    }

    /** The cell's own emphasis, independent of the row's (FR-045). */
    cellEmphasis(row: IRichBlockTableRow, column: IRichBlockTableColumn): RichBlockStatus | undefined {
        return row._cell_emphasis?.[column.key];
    }

    /**
     * The value of a `status` cell, when it is one the pill can render.
     *
     * An unknown value falls back to plain text rather than rendering an unlabelled pill: FR-026
     * requires severity to be readable as text, and a pill whose label we cannot resolve would
     * convey colour alone.
     */
    statusValue(row: IRichBlockTableRow, column: IRichBlockTableColumn): RichBlockStatus | undefined {
        const value = this.rawValue(row, column);
        // RICH_BLOCK_STATUSES rather than a local copy: a severity added there must reach the
        // pill, not quietly fall through to plain text here.
        return typeof value === 'string' && (RICH_BLOCK_STATUSES as readonly string[]).includes(value)
            ? (value as RichBlockStatus)
            : undefined;
    }

    /**
     * The cell as it reads, formatted for its column's `kind`.
     *
     * A value whose type does not match its kind is rendered as plain text rather than dropped:
     * the server has already validated the block, so a mismatch here is a disagreement between
     * two layers, and showing the value loses less than showing a blank cell.
     */
    displayValue(row: IRichBlockTableRow, column: IRichBlockTableColumn): string {
        const value = this.rawValue(row, column);
        if (value === undefined || value === null || value === '') {
            return '';
        }
        // Before the non-number return: the contract sends a `datetime` as an RFC 3339 or
        // `YYYY-MM-DD` STRING, so the switch below never saw one and the cell showed raw.
        if (column.kind === 'datetime') {
            return this.displayDatetime(value);
        }
        if (typeof value !== 'number') {
            return String(value);
        }

        switch (column.kind) {
            case 'percent':
                return `${this.decimal.transform(value)}%`;
            case 'bytes':
                // FormatSizePipe labels any non-positive value 'G' (`bytes > 0 ? units[index]
                // : 'G'`), so an empty volume reads as "0.0 G" — gigabytes of nothing.
                return value === 0 ? '0 B' : (this.size.transform(value, 1) ?? String(value));
            case 'number':
                return this.decimal.transform(value) ?? String(value);
            default:
                return String(value);
        }
    }

    /**
     * A `datetime` cell as local text.
     *
     * A date-only value is formatted in UTC on purpose. `new Date('2026-10-14')` is UTC midnight,
     * so formatting it in a negative offset renders the 13th — a date the payload never carried.
     * A value that does not parse is shown as it arrived rather than as "Invalid Date".
     */
    private displayDatetime(value: RichBlockCellValue): string {
        if (typeof value === 'string' && DATE_ONLY.test(value)) {
            return new Date(`${value}T00:00:00Z`).toLocaleDateString(undefined, {
                timeZone: 'UTC',
            });
        }
        const parsed = new Date(value as string | number);
        return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
    }

    /**
     * Order two cells of one column. Numbers compare numerically, everything else as text.
     *
     * A missing cell sorts last in BOTH directions rather than reading as zero or empty string:
     * "no value" is not the smallest value, and keeping the rows that have data together at the
     * top is what makes the column comparable.
     *
     * Which is why `descending` is applied here and not by the caller. Negating this function's
     * whole result flips the missing rows to the top on a descending sort -- the first version
     * did exactly that, and the test below caught it.
     */
    private compare(
        a: IRichBlockTableRow,
        b: IRichBlockTableRow,
        column: IRichBlockTableColumn,
        descending: boolean,
    ): number {
        const left = this.rawValue(a, column);
        const right = this.rawValue(b, column);
        const leftMissing = left === undefined || left === '';
        const rightMissing = right === undefined || right === '';
        if (leftMissing || rightMissing) {
            if (leftMissing && rightMissing) {
                return 0;
            }
            return leftMissing ? 1 : -1;
        }

        const order =
            typeof left === 'number' && typeof right === 'number'
                ? left - right
                : String(left).localeCompare(String(right), undefined, { numeric: true });
        return descending ? -order : order;
    }

    private rawValue(row: IRichBlockTableRow, column: IRichBlockTableColumn): RichBlockCellValue | undefined {
        const value = row[column.key];
        // A row is an index signature, so `_ref` and `_cell_emphasis` are typed into it. A column
        // key that collided with one of those would hand the template an object to interpolate.
        return typeof value === 'string' || typeof value === 'number' ? value : undefined;
    }
}
